import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Ban, CheckCircle2, RefreshCw, Search, XCircle } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getRequestBadgeClass = (status) => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'APPROVED') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
  if (normalized === 'REJECTED') return 'border-red-500/30 bg-red-500/10 text-red-300';
  return 'border-amber-500/30 bg-amber-500/10 text-amber-300';
};

export default function TransactionCancel() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';
  const [transactions, setTransactions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewingId, setReviewingId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [reason, setReason] = useState('');

  const selectedTransaction = useMemo(
    () => transactions.find((item) => `${item.target_type}:${item.target_id}` === selectedKey),
    [selectedKey, transactions]
  );

  const pendingRequests = useMemo(
    () => requests.filter((item) => String(item.status || '').toUpperCase() === 'PENDING'),
    [requests]
  );

  const filteredTransactions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return transactions.slice(0, 80);
    return transactions
      .filter((item) =>
        [
          item.transaction_number,
          item.title,
          item.subtitle,
          item.target_type,
          item.status,
          item.payment_method,
          item.merchant_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query)
      )
      .slice(0, 80);
  }, [searchQuery, transactions]);

  const fetchData = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const [transactionRes, requestRes] = await Promise.all([
        axios.get(`${API}/transaction-cancel/transactions`, { withCredentials: true }),
        axios.get(`${API}/transaction-cancel/requests`, { withCredentials: true }),
      ]);
      setTransactions(Array.isArray(transactionRes.data?.transactions) ? transactionRes.data.transactions : []);
      setRequests(Array.isArray(requestRes.data?.requests) ? requestRes.data.requests : []);
    } catch (error) {
      console.error('Failed to fetch cancel transaction data:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil data cancel transaksi.');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const submitCancelRequest = async (event) => {
    event.preventDefault();
    if (!selectedTransaction) {
      alert('Pilih transaksi yang mau dibatalkan.');
      return;
    }
    if (reason.trim().length < 3) {
      alert('Isi alasan pembatalan.');
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(
        `${API}/transaction-cancel/requests`,
        {
          target_type: selectedTransaction.target_type,
          target_id: selectedTransaction.target_id,
          reason: reason.trim(),
        },
        { withCredentials: true }
      );
      setReason('');
      setSelectedKey('');
      await fetchData(false);
      alert(isAdmin ? 'Transaksi berhasil dibatalkan.' : 'Request cancel sudah dikirim ke admin.');
    } catch (error) {
      console.error('Failed to submit cancel request:', error);
      alert(error.response?.data?.detail || 'Gagal mengirim request cancel.');
    } finally {
      setSubmitting(false);
    }
  };

  const reviewRequest = async (requestItem, action) => {
    const requestId = String(requestItem?.id || '').trim();
    if (!requestId) return;
    const label = action === 'approve' ? 'approve' : 'reject';
    if (!window.confirm(`${label === 'approve' ? 'Approve' : 'Reject'} cancel transaksi ini?`)) return;
    try {
      setReviewingId(requestId);
      await axios.post(`${API}/transaction-cancel/requests/${requestId}/${action}`, {}, { withCredentials: true });
      await fetchData(false);
    } catch (error) {
      console.error(`Failed to ${action} cancel request:`, error);
      alert(error.response?.data?.detail || `Gagal ${label} request cancel.`);
    } finally {
      setReviewingId('');
    }
  };

  return (
    <DashboardLayout>
      <Header title="Cancel Transaksi" />
      <div className="space-y-5 p-6 lg:p-8" data-testid="transaction-cancel-page">
        <div className="flex flex-col gap-4 rounded-sm border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),rgba(9,9,11,0)_42%),linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))] p-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Badge className="mb-3 rounded-sm border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/10">
              Approval
            </Badge>
            <h2 className="text-2xl font-semibold tracking-tight text-white">Cancel Transaksi</h2>
            <p className="mt-1 text-sm text-zinc-400">
              Tarik transaksi yang sudah diinput, isi alasan pembatalan, lalu proses approval admin.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => fetchData()}
            className="rounded-sm border border-white/20 bg-white/[0.03] text-white hover:bg-white/[0.08]"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-sm border border-white/10 bg-zinc-950/45 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Transaksi Bisa Ditarik</p>
            <p className="mt-2 text-2xl font-semibold text-white">{transactions.length}</p>
          </div>
          <div className="rounded-sm border border-amber-500/20 bg-amber-500/10 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200/70">Pending Approval</p>
            <p className="mt-2 text-2xl font-semibold text-amber-200">{pendingRequests.length}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-zinc-950/45 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Riwayat Request</p>
            <p className="mt-2 text-2xl font-semibold text-white">{requests.length}</p>
          </div>
        </div>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,.9fr)]">
          <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45">
            <div className="border-b border-white/10 px-5 py-4">
              <h3 className="text-base font-semibold text-white">Tarik Transaksi</h3>
              <p className="mt-1 text-xs text-zinc-500">Pilih transaksi payment atau order POS yang ingin dibatalkan.</p>
              <div className="relative mt-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Cari nomor, nama, nominal, merchant..."
                  className="h-10 rounded-sm border-white/10 bg-black/30 pl-10 text-white"
                />
              </div>
            </div>
            <div className="max-h-[520px] divide-y divide-white/5 overflow-auto">
              {loading ? (
                <div className="py-12 text-center text-sm text-zinc-500">Memuat transaksi...</div>
              ) : filteredTransactions.length > 0 ? (
                filteredTransactions.map((item) => {
                  const itemKey = `${item.target_type}:${item.target_id}`;
                  const selected = selectedKey === itemKey;
                  const hasPending = Boolean(item.pending_cancel_request_id);
                  return (
                    <button
                      type="button"
                      key={itemKey}
                      onClick={() => !hasPending && setSelectedKey(itemKey)}
                      disabled={hasPending}
                      className={`grid w-full gap-3 px-5 py-4 text-left transition-colors md:grid-cols-[1fr_140px_120px] md:items-center ${
                        selected ? 'bg-[#D4AF37]/10' : 'hover:bg-white/[0.03]'
                      } ${hasPending ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <span className="rounded-sm border border-white/10 bg-white/[0.03] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                            {item.target_type === 'PAYMENT' ? 'Payment' : 'Order'}
                          </span>
                          {hasPending ? (
                            <span className="rounded-sm border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-300">
                              Pending
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-2 truncate font-mono text-xs font-semibold text-zinc-100">
                          {item.transaction_number || item.target_id}
                        </p>
                        <p className="mt-1 truncate text-sm text-white">{item.title || '-'}</p>
                        <p className="mt-1 truncate text-xs text-zinc-500">{item.subtitle || item.payment_method || '-'}</p>
                      </div>
                      <p className="font-semibold text-[#F3D77A]">{formatCurrency(item.amount)}</p>
                      <div className="text-xs text-zinc-500 md:text-right">
                        <p>{item.status || '-'}</p>
                        <p className="mt-1">{formatDateTime(item.created_at)}</p>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="py-12 text-center text-sm text-zinc-500">Tidak ada transaksi yang cocok.</div>
              )}
            </div>
          </section>

          <section className="rounded-sm border border-white/10 bg-zinc-950/45 p-5">
            <form onSubmit={submitCancelRequest} className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-white">Alasan Pembatalan</h3>
                <p className="mt-1 text-xs text-zinc-500">
                  Request kasir akan masuk ke admin. Admin otomatis membatalkan saat approve.
                </p>
              </div>
              <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-600">Transaksi Dipilih</p>
                {selectedTransaction ? (
                  <div className="mt-2">
                    <p className="font-mono text-xs font-semibold text-zinc-100">
                      {selectedTransaction.transaction_number || selectedTransaction.target_id}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-white">{selectedTransaction.title}</p>
                    <p className="mt-1 text-xs text-zinc-500">{formatCurrency(selectedTransaction.amount)}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-zinc-500">Belum ada transaksi dipilih.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Alasan</Label>
                <Textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Contoh: salah input nominal, salah pilih produk, customer batal..."
                  className="min-h-32 rounded-sm border-white/10 bg-black/30 text-white"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !selectedTransaction || reason.trim().length < 3}
                className="w-full rounded-sm bg-red-500/15 text-red-200 ring-1 ring-red-500/30 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Ban className="mr-2 h-4 w-4" />
                {submitting ? 'Mengirim...' : isAdmin ? 'Batalkan Transaksi' : 'Kirim Request Cancel'}
              </Button>
            </form>
          </section>
        </div>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div>
              <h3 className="text-base font-semibold text-white">Approval Cancel</h3>
              <p className="mt-1 text-xs text-zinc-500">Riwayat request pembatalan transaksi.</p>
            </div>
            <p className="text-xs text-zinc-500">{requests.length} request</p>
          </div>
          <div className="max-h-[560px] divide-y divide-white/5 overflow-auto">
            {requests.length > 0 ? (
              requests.map((item) => {
                const target = item.target || {};
                const isPending = String(item.status || '').toUpperCase() === 'PENDING';
                return (
                  <div key={item.id} className="grid gap-4 px-5 py-4 lg:grid-cols-[1fr_180px_auto] lg:items-center">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-sm border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${getRequestBadgeClass(item.status)}`}>
                          {item.status}
                        </span>
                        <span className="rounded-sm border border-white/10 bg-white/[0.03] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                          {item.target_type === 'PAYMENT' ? 'Payment' : 'Order'}
                        </span>
                      </div>
                      <p className="mt-2 truncate font-mono text-xs font-semibold text-zinc-100">
                        {target.transaction_number || item.target_id}
                      </p>
                      <p className="mt-1 truncate text-sm text-white">{target.title || '-'}</p>
                      <p className="mt-1 text-xs leading-5 text-zinc-500">Alasan: {item.reason || '-'}</p>
                      <p className="mt-1 text-xs text-zinc-600">Dibuat oleh {item.created_by || '-'} pada {formatDateTime(item.created_at)}</p>
                    </div>
                    <div className="text-sm lg:text-right">
                      <p className="font-semibold text-[#F3D77A]">{formatCurrency(target.amount)}</p>
                      <p className="mt-1 text-xs text-zinc-500">{target.status || '-'}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      {isPending && isAdmin ? (
                        <>
                          <Button
                            type="button"
                            onClick={() => reviewRequest(item, 'approve')}
                            disabled={reviewingId === item.id}
                            className="h-9 rounded-sm bg-emerald-500/15 px-3 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-emerald-500/20 disabled:cursor-wait disabled:opacity-60"
                          >
                            <CheckCircle2 className="mr-1.5 h-4 w-4" />
                            Approve
                          </Button>
                          <Button
                            type="button"
                            onClick={() => reviewRequest(item, 'reject')}
                            disabled={reviewingId === item.id}
                            className="h-9 rounded-sm bg-red-500/15 px-3 text-xs font-semibold text-red-300 ring-1 ring-red-500/30 hover:bg-red-500/20 disabled:cursor-wait disabled:opacity-60"
                          >
                            <XCircle className="mr-1.5 h-4 w-4" />
                            Reject
                          </Button>
                        </>
                      ) : (
                        <span className="rounded-sm border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-zinc-500">
                          {item.approved_at ? `Approved ${formatDateTime(item.approved_at)}` : item.rejected_at ? `Rejected ${formatDateTime(item.rejected_at)}` : 'Menunggu admin'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-12 text-center text-sm text-zinc-500">Belum ada request cancel transaksi.</div>
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
